import type * as api from '@gitbook/api-client';
import type { DeepPartial } from '@gitbook/util-ts';

/**
 * The next steps offered once the organization agent has changes in an open change request. The
 * app shows each as a button that sends its label, and the agent offers them and acts on that
 * message as described, so both read them from here.
 */
export const CHANGE_REQUEST_NEXT_STEPS = [
    { label: 'Merge the changes', meaning: 'is the required confirmation to merge' },
    {
        label: 'Review the change request',
        meaning: 'asks you to inspect and report on the change request before anything is merged',
    },
] as const;

/**
 * Generic stream interface for use in agent RPC methods when they want to stream results
 * back to the client via CapnWeb callback passing.
 *
 * TResult must be an object with arbitrary properties representing each chunk of data
 * sent from the DO to the client.
 */
export interface AgentRpcStream<TResult extends object = {}> {
    /**
     * Send a chunk of data to the client.
     */
    onChunk(partialResult: DeepPartial<TResult>);

    /**
     * Signal that the stream has completed.
     */
    onEnd();

    /**
     * Signal that an error occurred during streaming.
     */
    onError(error: { message: string });
}

/**
 * Options controlling how the agent builds its scope and system prompt for a run.
 */
export type AgentScopeOptions = {
    /**
     * Whether to preload the site style guide — its entry page and table of contents — into the
     * system prompt so the agent has the house rules in context from the first step. Defaults to
     * true. Background tasks that don't write documentation, such as summarizing a change request
     * into its title and description, pass `false` to keep the prompt focused.
     */
    loadStyleguide?: boolean;
};

/**
 * Public Rpc API for any agent.
 */
export type AgentRpc = {
    /**
     * Post a message to the chat.
     */
    postMessage(message: api.AIMessageInput): Promise<void>;

    /**
     * Abort the current response if it's still streaming. The queue of pending messages
     * survives: discarding one is a per-message decision, through {@link unqueueMessage}.
     */
    stop(): Promise<void>;

    /**
     * Remove a message from the queue, returning it so the caller can put it back in the
     * composer. Rejects when the entry is gone — the agent already picked it up.
     */
    unqueueMessage(input: { id: string }): Promise<api.AIMessageInput>;

    /**
     * Interrupt the current response and answer the queued message right away, alone. The rest
     * of the queue keeps its place and follows once that response finishes. Resolves as done when
     * the agent already picked the entry up — the message is running either way.
     */
    sendQueuedMessageNow(input: { id: string }): Promise<void>;

    /**
     * Run a one-off background task from a custom prompt. The agent works through its normal tools
     * (the task is not appended to the conversation). Resolves as soon as the task is scheduled,
     * not when it completes: track progress through the agent's broadcast state (the agent goes
     * `Processing` while the task runs) and read its result from wherever the tools write it (for
     * example a change request title through the realtime channel).
     *
     * Idempotent: if a task is already running on this durable object the call is a no-op, so
     * several clients pointed at the same conversation converge on the one running task. Run tasks
     * in parallel by pointing each at its own conversation (its own durable object).
     *
     * Pass `scopeOptions` to tune how the agent's scope and system prompt are built for the task —
     * for example `{ loadStyleguide: false }` to skip preloading the style guide for a task that
     * doesn't write documentation.
     */
    runTask(prompt: string, scopeOptions?: AgentScopeOptions): Promise<void>;
};

/**
 * Rpc methods shared by the agents whose conversations carry an edit scope.
 */
export type AgentEditScopeRpc = {
    /**
     * Set the scope of the current conversation.
     */
    setEditScope(scope: api.AgentEditScope): Promise<void>;
};

/**
 * Public Rpc API for the AgentChangeRequest.
 */
export type AgentChangeRequestRpc = AgentEditScopeRpc & {
    /** Schedule generation and report failure through the agent status. */
    generateDetails(): Promise<void>;

    /**
     * Have the agent implement the change request on its own, from the title and description it
     * was opened with.
     *
     * Runs the same autonomous job an integration triggers when it opens a change request: the
     * change request stays a draft while the agent works, is marked ready for review when it
     * finishes, and is archived if the agent concludes it cannot be implemented. Resolves as soon
     * as the run is scheduled, not when it completes — follow it through the agent's broadcast
     * state, which goes `Processing` for the duration.
     *
     * Rejects when the caller may not edit the space, and when a run is already going in this
     * conversation: one run per conversation, so start each in its own.
     */
    implementChangeRequest(input: {
        /** Title of the change request, which is what the agent is asked to implement. */
        subject: string;
        /** Its description, as stored on the change request. Omitted when it has none. */
        description?: api.JSONDocument;
    }): Promise<void>;
};

/**
 * Public Rpc API for the AgentOrganization.
 */
export type AgentOrganizationRpc = AgentEditScopeRpc & {
    /**
     * Tell the agent where the user is, so it can resolve "this site" or "this page" without going
     * looking for them.
     *
     * Sent over RPC rather than carried on the connection: a conversation is one client and one
     * outbox for as long as it is open, and putting the location in the socket URL made navigating
     * mint a second client for the same conversation — stranding anything unsent under the previous
     * location. Called on connect and again whenever the user moves.
     */
    setLocation(input: {
        /**
         * Path the user is on, as they see it. Says what the ids do not — which page, which mode,
         * which tab — so a request phrased in terms of what is on screen resolves. Reduced to a
         * path and length-capped on arrival, since the address bar is filled by whatever link the
         * user clicked.
         */
        url?: string;
        siteId?: string;
        spaceId?: string;
        changeRequestId?: string;
        /** Set by the full-screen agent screen, which offers follow-up suggestions. */
        surface?: 'agent_screen';
    }): Promise<void>;

    /**
     * Work in a change request the user picked, rather than one the agent opened for itself.
     *
     * Does what the agent's own `attachChangeRequest` tool does: the change request becomes the
     * conversation's single writable target, so the editing tools resolve to it without the user
     * having to name it. It is a property of the conversation rather than of the message, so it
     * outlives a reload, a hibernation and navigating away from the change request.
     *
     * Attaching a second one replaces the first: exactly one target can be written to at a time.
     */
    attachChangeRequest(input: {
        spaceId: string;
        /**
         * Its id, or the number it is addressed by in the application URL — which is what a client
         * reading the route has, and the only thing it has without a request of its own.
         */
        changeRequest: string | number;
    }): Promise<void>;

    /**
     * Work in a space the user is editing live, with no change request in between.
     *
     * The counterpart of {@link attachChangeRequest} for a space whose admin has unlocked its live
     * edits: the application edits such a space in place, so the conversation writes to its primary
     * branch the same way. Rejected for any other space — the space's own edit mode decides this,
     * not the client.
     */
    attachSpace(input: { spaceId: string }): Promise<void>;

    /**
     * Show in the conversation that its change request was merged, when that happened outside it:
     * by a teammate, in the application or in another conversation. Checked against the change
     * request itself, and shown once.
     */
    recordChangeRequestMerged(): Promise<void>;
};

/** Whether the conversation shows the merge of the change request, by the agent or recorded since. */
export function hasChangeRequestMergeEvent(
    messages: api.AIMessage[],
    changeRequestId: string
): boolean {
    return messages.some((message) =>
        message.steps.some((step) =>
            step.toolCalls?.some(
                (toolCall) =>
                    toolCall.tool === 'mergeChangeRequest' &&
                    toolCall.changeRequestId === changeRequestId
            )
        )
    );
}
